import { updateAllIsDeleted, updateAllIsRead, updateIsRead } from 'client/api/notifications'
import useGetNotifications from 'client/hooks/useGetNotifications'
import { l } from 'client/Other/Translation'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { iUpdateNotificationObject } from '../../../client/Interfaces/notifications/NotificationsInterface'
import { Notification } from './Notification'
import './NotificationsModal.scss'

interface INotificationsModalProps {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const NotificationsModal: React.FunctionComponent<INotificationsModalProps> = (props) => {
  const numberToFetch = 20
  const [pageNumber, setPageNumber] = useState(1)
  const [viewDeleted, setViewDeleted] = useState(false)
  const [hideNotifications, setHideNotifications] = useState(false)
  const [showEmpty, setShowEmpty] = useState(false)

  // Call the custom hook and destructure the returned object
  const { loading, error, notifications, lastEvaluatedKey } = useGetNotifications(
    {
      size: numberToFetch,
      isDeleted: viewDeleted,
    },
    pageNumber,
  )

  // Reduces the notifications array to only the unread ones to make a smaller
  // api call when setting them to read.
  useEffect(() => {
    return () => {
      const unreadNotifications = notifications.reduce((accumulator, notification) => {
        if (!notification.isRead) {
          return [...accumulator, { ...notification, isRead: true }]
        }
        return accumulator
      }, [])
      if (unreadNotifications.length > 0) {
        updateIsRead({ notifications: unreadNotifications } as iUpdateNotificationObject)
      }
    }
  }, [notifications])

  // Used to watch for the last div of the list appearing on screen
  const observer = useRef<IntersectionObserver | null>(null)

  // The callback works with the observer ref to reset the reference
  // after each update so that the ref always points to the last element
  // created from the notifications array
  const lastNotificationElementRef = useCallback(
    (node) => {
      if (loading) {
        return
      }
      if (observer.current) {
        observer.current.disconnect()
      }
      observer.current = new IntersectionObserver((entries) => {
        // If we have a last evaluated key then we want to trigger the
        // api call again.
        if (entries[0].isIntersecting && lastEvaluatedKey) {
          setPageNumber((prev) => prev + 1)
        }
      })
      if (node) {
        observer.current.observe(node)
      }
    },
    [loading, lastEvaluatedKey],
  )

  // This handles the clicks on the button to delete all or
  // restore all notifications
  const handleUpdateAllIsDeleted = (deleteAll: boolean) => {
    updateAllIsDeleted(deleteAll)
    setHideNotifications(true)
    setShowEmpty(true)
    if (deleteAll) {
      updateAllIsRead()
    }
  }

  // This handles the filters for viewing all or deleted
  const handleHeaderFilters = (deleted: boolean) => {
    setViewDeleted(deleted)
    setHideNotifications(false)
    setShowEmpty(false)
  }

  // Maps notifications array to jsx
  const formatNotifications = () => {
    if (notifications.length === 0) {
      return
    }

    // Use flatMap instead of map to remove unwanted values
    const notificationJSX = notifications.flatMap((notification, index) => {
      if (!notification.isDeleted === viewDeleted) {
        return []
      }

      // If it's the last element, we return a Notification with the
      // callback attached to set the ref of the div.
      if (notifications.length === index + 1) {
        return (
          <Notification
            notification={notification}
            setOpen={props.setOpen}
            key={'notification' + index}
            innerRef={lastNotificationElementRef}
          />
        )
      }

      return <Notification notification={notification} setOpen={props.setOpen} key={'notification' + index} />
    })
    return notificationJSX
  }

  return (
    <div className={'notifications-modal__container'}>
      <div className="notifications-modal__content-container">
        <div className="notifications-modal__content">
          <div className="notifications-modal__header">
            <div className="notifications-modal__header-title">{l('notifications.modal.header.title')}</div>
            <div className="notifications-modal__header-actions">
              <div className="notifications-modal__header-filter">
                <div
                  className={'notifications-modal__header-filter-all' + (!viewDeleted ? ' selected' : '')}
                  data-automation="notifications-modal-show-all"
                  onClick={() => {
                    handleHeaderFilters(false)
                  }}
                >
                  <span>{l('notifications.modal.header.show.all')}</span>
                </div>
                <div
                  className={'notifications-modal__header-filter-deleted' + (viewDeleted ? ' selected' : '')}
                  data-automation="notifications-modal-show-deleted"
                  onClick={() => {
                    handleHeaderFilters(true)
                  }}
                >
                  <span>{l('notifications.modal.header.show.deleted')}</span>
                </div>
              </div>
              {!viewDeleted ? (
                <div
                  className="notifications-modal__header-delete"
                  onClick={() => {
                    handleUpdateAllIsDeleted(true)
                  }}
                >
                  {l('notifications.modal.header.delete.all')}
                </div>
              ) : (
                <div
                  className="notifications-modal__header-delete"
                  onClick={() => {
                    handleUpdateAllIsDeleted(false)
                  }}
                >
                  {l('notifications.modal.header.restore.all')}
                </div>
              )}
            </div>
          </div>
          {viewDeleted && (
            <div className={'notifications-modal__deleted-banner'}>{l('notifications.modal.delete.message')}</div>
          )}
          {/* Show notifications */}
          <span className={hideNotifications ? 'hidden' : ''}>{formatNotifications()}</span>
          {/* Show end of list or empty list icons */}
          {showEmpty || formatNotifications() === undefined ? (
            <div className={'notifications-modal__empty-list'}>
              <div className={'notifications-modal__empty-list__image'}></div>
              <div className={'notifications-modal__empty-list__text'}>{l('notifications.modal.empty.list')}</div>
            </div>
          ) : (
            <div className={'notifications-modal__end-of-list'}>
              <div className={'notifications-modal__end-of-list__image'}></div>
              <div className={'notifications-modal__end-of-list__text'}>{l('notifications.modal.end.list')}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
